<template>
  <div v-if="summaryData" class="weekly-summary-container">
    <div class="weekly-summary-wrapper">
      <!-- Header con icono y título -->
      <div class="summary-header">
        <div class="d-flex align-items-center">
          <div class="ai-icon me-3">
            <i class="fas fa-robot"></i>
          </div>
          <div class="flex-grow-1">
            <h3 class="mb-1">
              {{ $t('weekly_summary.title') }}
              <span v-if="summaryData.createdAt" class="text-muted" style="font-weight: normal; font-size: 0.9em;">
                - {{ $t('weekly_summary.update_label') }} {{ formatDate(summaryData.createdAt) }}
              </span>
            </h3>
            <p class="mb-0 text-muted">
              {{ $t('weekly_summary.subtitle') }}
              <span v-if="summaryData.startDate && summaryData.endDate">
                ({{ formatDate(summaryData.startDate) }} - {{ formatDate(summaryData.endDate) }})
              </span>
            </p>
          </div>
          <div class="ai-badge">
            <AIBadge />
          </div>
        </div>
      </div>

      <!-- Dos columnas: Estadísticas y Resumen Semanal -->
      <div class="row g-3 mt-0">
        <!-- Columna Estadísticas -->
        <div class="col-md-4">
          <div v-if="summaryData.statistics" class="summary-card-white">
            <h4 class="card-title">
              <i class="fas fa-chart-bar me-2"></i>
              {{ $t('weekly_summary.statistics_title') }}
            </h4>
            <div class="card-content statistics-content">
              <!-- Badge con total de actividades -->
              <div v-if="summaryData.totalMaterials" class="mb-3">
                <span class="badge bg-primary fs-6 px-3 py-2">
                  <strong>{{ summaryData.totalMaterials }}</strong> {{ $t('weekly_summary.activities_incorporated') }}
                </span>
              </div>
              <!-- Estadísticas formateadas -->
              <div v-html="formatStatistics(summaryData.statistics)"></div>
            </div>
          </div>
        </div>

        <!-- Columna Resumen Semanal -->
        <div class="col-md-8">
          <div v-if="summaryData.weeklySummary" class="summary-card-white">
            <h4 class="card-title">
              <i class="fas fa-file-alt me-2"></i>
              {{ $t('weekly_summary.full_summary_title') }}
            </h4>
            <div class="card-content">
              <div class="weekly-summary-full" v-html="summaryData.weeklySummary">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import HomeIAService from '@/services/HomeIAService'
import AIBadge from "@/components/AIBadge.vue";

export default {
  name: 'WeeklySummaryCard',
  components: { AIBadge },
  setup() {
    const summaryData = ref(null)
    const loading = ref(false)
    const error = ref(null)
    const { locale } = useI18n()

    const fetchWeeklySummary = async () => {
      loading.value = true
      error.value = null
      try {
        const data = await HomeIAService.getLastWeeklySummary(locale.value)
        if (data && !data.error) {
          summaryData.value = data
        } else {
          summaryData.value = null
        }
      } catch (err) {
        console.error('Error fetching weekly summary:', err)
        if (err.response && err.response.status === 404) {
          summaryData.value = null
        } else {
          error.value = err.message || 'Error al cargar el resumen semanal'
          summaryData.value = null
        }
      } finally {
        loading.value = false
      }
    }

    // Volver a cargar si cambia el idioma
    watch(locale, () => {
      fetchWeeklySummary()
    })

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const localeMap = {
        'ca': 'ca-ES',
        'es': 'es-ES',
        'en': 'en-US'
      }
      const dateParts = dateString.split('-')
      if (dateParts.length === 3) {
        const date = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
        return date.toLocaleDateString(localeMap[locale.value] || 'es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      }
      const date = new Date(dateString)
      return date.toLocaleDateString(localeMap[locale.value] || 'es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const formatStatistics = (text) => {
      if (!text) return ''
      const lines = text.split('\n')
      let result = []

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        
        // Si la línea está vacía, la añadimos
        if (!line) {
          result.push('<br>')
          continue
        }

        // Si empieza por bullet (•), formatearla en negrita
        if (line.startsWith('•')) {
          // Extraer el texto después del bullet
          const textAfterBullet = line.substring(1).trim()
          // Añadir en negrita con salto de línea simple
          result.push(`<strong>• ${textAfterBullet}</strong><br>`)
        } else {
          // Para otras líneas, mantenerlas normales
          result.push(`${line}<br>`)
        }
      }

      return result.join('')
    }

    onMounted(() => {
      fetchWeeklySummary()
    })

    return {
      summaryData,
      loading,
      error,
      formatDate,
      formatStatistics,
      locale
    }
  }
}
</script>

<style scoped>
.weekly-summary-container {
  margin: 3rem 0;
}

.weekly-summary-wrapper {
  background-color: #f2f5f6;
  padding: 2rem;
  border: none;
}

.summary-header {
  margin-bottom: 1rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #d1d1d1;
}

.ai-icon {
  font-size: 2.5rem;
  color: #007BC1;
  background: white;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.weekly-summary-wrapper h3 {
  font-size: 1.75rem;
  font-weight: 500;
  color: #4A4A4A;
  margin: 0;
}

.weekly-summary-wrapper .text-muted {
  font-size: 0.95rem;
  color: #636363;
}

.ai-badge .badge {
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  font-weight: 500;
}

/* Cards blancas al estilo Aprèn */
.summary-card-white {
  background-color: white;
  border: 1px solid #e0e0e0;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease;
}

.summary-card-white:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 500;
  color: #4A4A4A;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #d1d1d1;
  padding-bottom: 0.75rem;
}

.card-title i {
  font-size: 1.1rem;
  color: #007BC1;
}

.card-content {
  flex-grow: 1;
  color: #636363;
  font-size: 1rem;
  line-height: 1.7;
  display: flex;
  flex-direction: column;
}

.statistics-content {
  line-height: 1.8;
}

.weekly-summary-full {
  white-space: pre-wrap;
  word-wrap: break-word;
}

@media (max-width: 768px) {
  .weekly-summary-wrapper {
    padding: 1.5rem;
  }

  .weekly-summary-wrapper h3 {
    font-size: 1.5rem;
  }

  .ai-icon {
    width: 50px;
    height: 50px;
    font-size: 2rem;
  }

  .card-title {
    font-size: 1.1rem;
  }

  .card-content {
    font-size: 0.95rem;
  }

  .summary-card-white {
    margin-bottom: 1.5rem;
  }
}
</style>
